import { SlateStatus } from '@betterpool/api-types/games-service';
import useTranslation from 'next-translate/useTranslation';
import { useCallback } from 'react';

import repeat from '~/common/utils/repeat';
import { UnstyledNextLink } from '~/components/shared/UnstyledNextLink/UnstyledNextLink';
import type { EntryStatus } from '~/domains/contest/domains/services/enums/entry-status.enum';
import type { PlayerPoolMember } from '~/domains/contest/domains/services/interfaces/player-pool-member.interface';
import { Routes } from '~/domains/common/constants/routes';
import { getCdnUrl } from '~/domains/assets/utils/getCdnUrl';

import EntryRow from '../Entry/EntryRow';
import EntryItem from '../Entry/EntryItem';

type MyEntriesContentProps = {
  contestId: string;
  entries?: {
    picks: PlayerPoolMember[];
    id: string;
    status: EntryStatus;
    name?: string;
  }[];
  slateStatus?: SlateStatus;
  leagueAlias: string;
  tiersCount: number;
  slateId: string;
  isEntertainmentOnly?: boolean;
  openEntryNameEditModal: (entryInfo: { id: string; name: string }) => void;
};

function MyEntriesContent({
  contestId,
  entries = [],
  slateStatus,
  slateId,
  leagueAlias,
  tiersCount,
  isEntertainmentOnly,
  openEntryNameEditModal,
}: MyEntriesContentProps) {
  const { t } = useTranslation('contest');
  const getPicksheetHref = useCallback(
    (entryId: string) =>
      Routes.gamecenterPicks(
        contestId,
        {
          entryId,
          slateId,
        }
      ),
    [contestId, slateId]
  );

  const getOptions = useCallback(
    (id: string, name: string) => ({
      editName: {
        title: t('detail.entries.entry.option.editName'),
        onClick: () => openEntryNameEditModal({ id, name }),
      },
      edit: {
        title:
          slateStatus === SlateStatus.SCHEDULED
            ? t('detail.entries.entry.option.edit')
            : t('detail.entries.entry.option.view'),
        href: getPicksheetHref(id),
      },
      withdrawal: {
        title: t('detail.entries.entry.option.withdraw'),
        isHidden: slateStatus !== SlateStatus.SCHEDULED,
      },
    }),
    [getPicksheetHref, openEntryNameEditModal, slateStatus, t]
  );

  return (
    <>
      {entries.map((entry) => (
        <EntryRow
          key={entry.id}
          name={entry.name}
          entryId={entry.id}
          contestId={contestId}
          isEntertainmentOnly={isEntertainmentOnly}
          options={getOptions(entry.id, entry.name)}
        >
          {entry?.picks &&
            entry?.picks.map((pick) => (
              <UnstyledNextLink href={getPicksheetHref(entry.id)} key={pick.id} $colorInherit>
                <EntryItem
                  key={pick.id}
                  name={pick.name}
                  image={getCdnUrl(`/${leagueAlias}/${pick.playerId}.png`)}
                />
              </UnstyledNextLink>
            ))}
          {!entry?.picks &&
            repeat(tiersCount, (tierIndex) => (
              <UnstyledNextLink href={getPicksheetHref(entry.id)} key={tierIndex} $colorInherit>
                <EntryItem name={`Tier ${tierIndex + 1}`} />
              </UnstyledNextLink>
            ))}
        </EntryRow>
      ))}
    </>
  );
}

export default MyEntriesContent;
